import { Link } from "react-router-dom";
import { Bot, ExternalLink, Link2, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Kicker } from "@/components/ui/kicker";
import {
  FindingTriageMenu,
  ExportFindingButton,
} from "@/components/experience/finding-actions";
import { ProviderIcon } from "@/components/workflow/workflow-rail";
import { FindingObservation } from "@/components/experience/finding-observation";
import { MarkdownProse } from "@/components/ui/markdown-prose";
import { WalkthroughLinkedIssues } from "./walkthrough-linked-issues";
import { WorkspaceFindingStepSection } from "./workspace-finding-step-section";
import {
  type WalkthroughFinding,
  type ActiveStepInfo,
  type WorkspaceFindingDrawerProps,
  toTriageState,
} from "./workspace-types";

const PROVIDER_NAMES: Record<string, string> = {
  linear: "Linear",
  jira: "Jira",
  github: "GitHub",
};

export function WorkspaceFindingDrawer({
  activeFinding,
  activeStep,
  walkthroughId,
  projectId,
  canEdit,
  hasLinkedRepo,
  isTrackerConfigured,
  activeProvider,
  exportConfig,
  createIssuePending,
  onClose,
  onCreateIssue,
  onLinkExisting,
  onViewInTrace,
  onOpenZen,
}: WorkspaceFindingDrawerProps) {
  const workRequests = useWorkRequests(projectId);
  const highlights = activeFinding?.targetBox
    ? [
        {
          id: "walkthrough-finding-target",
          box: activeFinding.targetBox,
          tone: activeFinding.severity || activeFinding.type,
          label:
            activeFinding.title ??
            (activeFinding.type === "helps" ? "Strength" : "Issue"),
          isActive: true,
        },
      ]
    : undefined;

  return (
    <Sheet open={!!activeFinding} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl md:max-w-2xl overflow-y-auto p-0 gap-0"
        showCloseButton={false}
      >
        <SheetTitle className="sr-only">Finding detail</SheetTitle>
        {activeFinding && (
          <article aria-labelledby="walkthrough-finding-title">
            <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-xs border-b border-border/40 px-6 py-3.5 space-y-1.5 shadow-2xs">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                  <Kicker as="h3">
                    {activeFinding.type === "helps"
                      ? "Observed strength"
                      : "Selected finding"}
                  </Kicker>
                  {activeFinding.type === "helps" ? (
                    <Badge
                      variant="outline"
                      className="text-xs border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-medium"
                    >
                      Strength
                    </Badge>
                  ) : activeFinding.severity ? (
                    <Badge variant="outline" className="text-xs">
                      {activeFinding.severity}
                    </Badge>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 size-9 sm:size-8 rounded-full"
                  onClick={onClose}
                  aria-label="Close the detail pane"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              <h2
                id="walkthrough-finding-title"
                className="text-base sm:text-lg font-semibold text-foreground leading-snug line-clamp-2"
              >
                {activeFinding.title || activeFinding.description}
              </h2>
            </header>

            <div className="p-6 space-y-5">
              {/* Linked issues badge (if canonical) */}
              <WalkthroughLinkedIssues
                canonicalId={activeFinding.canonicalFindingId}
              />

              {activeFinding.type === "helps" ? (
                <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-800 dark:text-emerald-300">
                  <Sparkles className="size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span className="font-medium">
                    This positive signal was highlighted during the walkthrough
                    as a behavior that respects the persona&apos;s time or
                    expectations.
                  </span>
                </div>
              ) : (
                canEdit && (
                  <div className="flex flex-col gap-2 border-y border-border/60 py-3">
                    <div className="flex items-center gap-2">
                      <FindingTriageMenu
                        findingId={
                          activeFinding.canonicalFindingId ?? activeFinding.id
                        }
                        projectId={projectId}
                        state={toTriageState(activeFinding.triageStatus)}
                        findingType={activeFinding.type}
                        subject={
                          activeFinding.title ??
                          activeFinding.description.slice(0, 60)
                        }
                        size="sm"
                        className="w-full"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        disabled={createIssuePending || !isTrackerConfigured}
                        onClick={() => onCreateIssue(activeFinding)}
                        title={
                          !isTrackerConfigured
                            ? "Connect issue tracker in Project Settings"
                            : undefined
                        }
                      >
                        {createIssuePending ? (
                          <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                        ) : (
                          <ProviderIcon
                            provider={activeProvider || undefined}
                            className="mr-1.5 size-3.5"
                          />
                        )}
                        Create{" "}
                        {activeProvider
                          ? (PROVIDER_NAMES[activeProvider] ?? "GitHub")
                          : ""}{" "}
                        issue…
                      </Button>

                      {activeFinding.canonicalFindingId && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() =>
                            onLinkExisting(activeFinding.canonicalFindingId!)
                          }
                        >
                          <Link2 className="mr-1.5 size-3.5" />
                          Link existing work…
                        </Button>
                      )}

                      {activeFinding.canonicalFindingId && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          disabled={workRequests.isPending}
                          onClick={() => {
                            const isAddressed =
                              activeFinding.triageStatus === "addressed";
                            if (isAddressed) {
                              workRequests.queueVerify(
                                activeFinding.canonicalFindingId!,
                              );
                            } else {
                              workRequests.queueFix(
                                activeFinding.canonicalFindingId!,
                              );
                            }
                          }}
                        >
                          <Bot className="mr-1.5 size-3.5" />
                          Queue fix for agent
                        </Button>
                      )}

                      {exportConfig && projectId && (
                        <ExportFindingButton
                          findingId={activeFinding.id}
                          projectId={projectId}
                          exportConfig={exportConfig}
                          hasLinkedRepo={hasLinkedRepo}
                        />
                      )}
                    </div>
                  </div>
                )
              )}

              {/* Finding Observation: UI Fact and Consequence */}
              <section className="space-y-2" aria-label="Observation">
                <FindingObservation
                  uiFact={activeFinding.uiFact}
                  consequence={activeFinding.consequence}
                  what={activeFinding.what}
                  why={activeFinding.why}
                  basis={activeFinding.basis}
                  screen={activeFinding.screen}
                  breaksRule={activeFinding.breaksRule}
                  breaksFile={activeFinding.breaksFile}
                  description={activeFinding.description}
                  designCauses={activeFinding.designCauses}
                />
              </section>

              {/* Actionable Recommendation */}
              {activeFinding.recommendation && (
                <section
                  className="space-y-1.5 border-t border-border/60 pt-3"
                  aria-label="Recommendation"
                >
                  <h4 className="text-xs font-semibold text-foreground">
                    Recommendation
                  </h4>
                  <div className="rounded-md border border-border/70 bg-muted/20 p-3 text-xs">
                    <MarkdownProse
                      content={activeFinding.recommendation}
                      lineBreaks
                    />
                  </div>
                </section>
              )}

              {activeStep && (
                <WorkspaceFindingStepSection
                  activeStep={activeStep}
                  targetBox={activeFinding.targetBox}
                  targetTone={activeFinding.severity || activeFinding.type}
                  highlights={highlights}
                  onOpenZen={onOpenZen}
                  onClose={onClose}
                  onViewInTrace={onViewInTrace}
                  walkthroughId={walkthroughId}
                  projectId={projectId}
                />
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border/60">
                <Link
                  to={`/projects/${projectId}/findings/${activeFinding.id}`}
                  className="inline-flex items-center gap-1 text-sm underline underline-offset-2"
                >
                  Open full finding page
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>

                {exportConfig && projectId && (
                  <ExportFindingButton
                    findingId={activeFinding.id}
                    projectId={projectId}
                    exportConfig={exportConfig}
                    hasLinkedRepo={hasLinkedRepo}
                  />
                )}
              </div>
            </div>
          </article>
        )}
      </SheetContent>
    </Sheet>
  );
}
